import { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Avatar,
  Drawer,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  TextField,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'

export default function StaffDrawer({ open, onClose, staffList, scheduledIds, onAdd }) {
  const [search,  setSearch]  = useState('')
  const [role,    setRole]    = useState('All roles')
  // maxCost filter state kept for future use, input hidden in v1
  const [maxCost, setMaxCost] = useState('')

  useEffect(() => {
    if (open) {
      setSearch('')
      setRole('All roles')
      setMaxCost('')
    }
  }, [open])

  const roleOptions = useMemo(() => {
    const roles = [...new Set(staffList.map((s) => s.role).filter(Boolean))].sort()
    return ['All roles', ...roles]
  }, [staffList])

  const filtered = useMemo(() => {
    const q    = search.trim().toLowerCase()
    const cost = Number(maxCost)
    return staffList.filter((s) => {
      if (q && !s.name.toLowerCase().includes(q) && !s.role.toLowerCase().includes(q)) return false
      if (role !== 'All roles' && s.role !== role) return false
      // maxCost filter hidden in v1 but still active when set
      if (maxCost && !Number.isNaN(cost) && s.hourlyRate > cost) return false
      return true
    })
  }, [search, role, maxCost, staffList])

  return (
    <Drawer anchor="left" open={open} onClose={onClose} PaperProps={{ sx: { width: 340 } }}>
      <div className="staff-drawer">
        <div className="drawer-header d-flex align-items-center">
          <h6 className="m-0 flex-grow-1">Add Staff</h6>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseIcon fontSize="small" sx={{ color: '#d0021b' }} />
          </IconButton>
        </div>

        <div className="drawer-filters d-flex flex-column gap-2">
          <TextField
            size="small"
            fullWidth
            placeholder="Search name or role…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            size="small"
            fullWidth
            label="Role"
            value={role}
            onChange={(e) => setRole(e.target.value)}
          >
            {roleOptions.map((r) => (
              <MenuItem key={r} value={r}>{r}</MenuItem>
            ))}
          </TextField>
          {/* Max cost filter — hidden in v1, kept for future use */}
          <TextField
            size="small"
            fullWidth
            label="Max cost / hour"
            value={maxCost}
            onChange={(e) => setMaxCost(e.target.value)}
            inputProps={{ type: 'number', min: 0 }}
            style={{ display: 'none' }}
          />
        </div>

        <List dense disablePadding>
          {filtered.length === 0 && (
            <ListItem>
              <ListItemText
                secondary="No staff match your filters."
                secondaryTypographyProps={{ style: { fontSize: 12, color: '#999' } }}
              />
            </ListItem>
          )}
          {filtered.map((s) => {
            const isAdded = scheduledIds.includes(s.id)
            return (
              <ListItem
                key={s.id}
                className="staff-list-item"
                secondaryAction={
                  isAdded ? (
                    <CheckCircleIcon sx={{ color: '#2e9e5b', fontSize: 20 }} />
                  ) : (
                    <IconButton
                      size="small"
                      onClick={() => onAdd(s.id)}
                      aria-label={`Add ${s.name}`}
                    >
                      <AddCircleOutlineIcon sx={{ fontSize: 20, color: 'var(--brand-color-1)' }} />
                    </IconButton>
                  )
                }
              >
                <ListItemAvatar sx={{ minWidth: 44 }}>
                  <Avatar sx={{ width: 30, height: 30, fontSize: 12, bgcolor: 'var(--brand-color-1)' }}>
                    {s.initials}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={s.name}
                  secondary={s.role || '—'}
                  primaryTypographyProps={{ className: 'staff-name' }}
                  secondaryTypographyProps={{ className: 'staff-meta' }}
                />
              </ListItem>
            )
          })}
        </List>
      </div>
    </Drawer>
  )
}

StaffDrawer.propTypes = {
  open:         PropTypes.bool.isRequired,
  onClose:      PropTypes.func.isRequired,
  staffList:    PropTypes.array.isRequired,
  scheduledIds: PropTypes.array.isRequired,
  onAdd:        PropTypes.func.isRequired,
}
